import { TabContext } from "@mui/lab";
import { Box, Stack } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { Route, Routes, useLocation, useNavigate } from "react-router";
import {
  PrimaryButtonSettingsEditor,
  SecondaryButtonSettingsEditor,
} from "./editors/button-settings-editor";
import {
  PrimaryDividerSettingsEditor,
  SecondaryDividerSettingsEditor,
} from "./editors/divider-settings-editor";
import { FooterSettingsEditor } from "./editors/footer-settings-editor";
import { HeaderSettingsEditor } from "./editors/header-settings-editor";
import { HeaderTextSettingsEditor } from "./editors/header-text-settings-editor";
import { LayoutEditor } from "./editors/layout-editor";
import { LogoSettingsEditor } from "./editors/logo-settings-editor";
import { TemplateDetailsEditor } from "./editors/template-details-editor";
import { TextSettingsEditor } from "./editors/text-settings-editor";
import { EMailTemplateEditorTab } from "./email-template-editor-tab";
import { WatchedLayoutPreview } from "./preview/layout-preview";

import type { EmailDesignSchema } from "../email-design/types/email-design-type";
import type { BoxProps } from "@mui/material";
import type { FC } from "react";

export const EmailTemplateEditor: FC<BoxProps> = ({ children, ...props }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { control, getValues } = useFormContext<EmailDesignSchema>();
  useWatch({ control, name: "header" });
  const header = getValues("header");

  const tabs = [
    {
      name: "Details",
      component: <TemplateDetailsEditor />,
      value: "template_details",
    },
    {
      name: "Layout",
      component: <LayoutEditor />,
      value: "layout",
    },
    {
      name: "Header",
      component: <HeaderSettingsEditor />,
      value: "header",
    },
    {
      name: "Logo",
      component: <LogoSettingsEditor />,
      value: "logo",
      disabledTooltipText: `Enable "Use Logo" in the Header tab`,
    },
    {
      name: "Header Text",
      component: <HeaderTextSettingsEditor />,
      value: "header_text",
      disabledTooltipText: `Enable "Use Header Text" in the Header tab`,
    },
    {
      name: "Primary Button",
      component: <PrimaryButtonSettingsEditor />,
      value: "primary_button",
    },
    {
      name: "Secondary Button",
      component: <SecondaryButtonSettingsEditor />,
      value: "secondary_button",
    },
    {
      name: "Text",
      component: <TextSettingsEditor />,
      value: "text",
    },
    {
      name: "Footer",
      component: <FooterSettingsEditor />,
      value: "footer",
    },
    {
      name: "Primary Divider",
      component: <PrimaryDividerSettingsEditor />,
      value: "primary_divider",
    },
    {
      name: "Secondary Divider",
      component: <SecondaryDividerSettingsEditor />,
      value: "secondary_divider",
    },
  ];

  const defaultTab = tabs[0]!;

  const handleSelect = (editor: string): void => {
    if (tabs.map(({ value }) => value).includes(editor)) {
      void navigate(`/manage/brand/email_design/${editor}`);
    }
  };

  const pathTab = location.pathname.split("/").slice(-1)[0] ?? "/";
  const value = pathTab === "email_design" ? defaultTab.value : pathTab;

  return (
    <Box
      sx={{
        minWidth: 0,
        overflowX: "auto",
        pb: 1,
        width: "100%",
      }}
      display="grid"
      gridTemplateColumns="minmax(360px, 25%) minmax(800px, auto)"
      gap={4}
      {...props}
    >
      <Stack gap={2}>
        {children}
        <Box display="flex">
          <TabContext value={value}>
            <Stack direction="column" spacing={0} width="8em">
              {tabs.map((tab) => {
                const disabled =
                  (tab.value === "logo" && !header?.logo) ||
                  (tab.value === "header_text" && !header?.text);
                return (
                  <EMailTemplateEditorTab
                    key={tab.value}
                    selected={value === tab.value}
                    label={tab.name}
                    to={`/manage/brand/email_design/${tab.value}`}
                    disabled={disabled}
                    disabledTooltipText={tab.disabledTooltipText}
                  />
                );
              })}
            </Stack>
          </TabContext>
          <Box
            flexGrow={1}
            sx={(theme) => ({
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: `${theme.shape.borderRadius}px`,
              borderTopLeftRadius: 0,
              padding: theme.spacing(2),
              backgroundColor: theme.palette.background.paper,
            })}
          >
            <Routes>
              <Route path="/" element={defaultTab.component} />
              {tabs.map((tab) => (
                <Route key={tab.value} path={tab.value} element={tab.component} />
              ))}
            </Routes>
          </Box>
        </Box>
      </Stack>
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        borderRadius="10px"
        p={2}
        border={(theme) => `1px solid ${theme.palette.divider}`}
      >
        <WatchedLayoutPreview badgeProps={{ editor: value, onSelect: handleSelect }} />
      </Box>
    </Box>
  );
};
